import React, { useState } from 'react';
import { Plus, Info, PlayCircle } from 'lucide-react';
import { Alert, Button, Checkbox, TextInput, Textarea } from '@parsina/framework';
import { C, RADIUS } from '@parsina/framework/tokens';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { useMyPageStrings } from '../myPageStrings';
import { useDraft } from '../useDraft';
import {
  LIMITS, itemsOf, maxItems, canAddItem, newItemId, saveStatus, serviceImage, overLimit, sectionMeta,
} from '../MyPage.shared';
import { Counter, ImageSlot, RowActions, SaveRow, moveItem, useFieldStyle } from '../parts/common';
import { ImageErrors } from './HeroEditor';

// ─────────────────────────────────────────────────────────────────────────────
// The three repeatable sections — خدمات, نظرات منتخب, ویدیو. Each is a list of
// items saved as ONE section write (the section's content_json), so reordering
// and editing an item are one «ذخیره», and the counters show the manifest's
// limit before the service has to refuse it.
// ─────────────────────────────────────────────────────────────────────────────

const itemBox = {
  border: `1px solid ${C.border}`, borderRadius: RADIUS.card, padding: '0.875rem', display: 'grid', gap: '0.75rem',
  background: C.card,
};

function useItemsDraft(section) {
  return useDraft(itemsOf(section));
}

function ItemsHeader({ kind, count }) {
  const s = useMyPageStrings();
  const { formatNumber } = useTranslation();
  const max = maxItems(kind);
  return (
    <div style={{ fontSize: '0.75rem', color: count > max ? C.red : C.textSecondary }} data-testid={`my-page-${kind}-count`}>
      {s('myPage.items.count', { count: formatNumber(count), max: formatNumber(max) })}
    </div>
  );
}

function useItemsSave(kind, actions, adopt) {
  return async (items) => {
    const res = await actions.updateSection(kind, kind, { content: { items } });
    if (res.ok) adopt(itemsOf(res.page?.sections?.find((x) => x.kind === kind)));
    return res;
  };
}

// ── خدمات ───────────────────────────────────────────────────────────────────

export function ServicesEditor({ page, section, actions, cards, readOnly }) {
  const s = useMyPageStrings();
  const fieldStyle = useFieldStyle();
  const { draft, setDraft, dirty, adopt, reset } = useItemsDraft(section);
  const saveItems = useItemsSave('services', actions, adopt);
  const card = cards.services || {};
  const savedIds = new Set(itemsOf(section).map((i) => i.id));
  const patch = (idx, k, v) => setDraft((items) => items.map((it, i) => (i === idx ? { ...it, [k]: v } : it)));
  const invalid = draft.some((it) => !String(it.title || '').trim()
    || overLimit(it.title, LIMITS.service_title_max)
    || overLimit(it.description_md, LIMITS.service_description_max))
    || draft.length > maxItems('services');

  return (
    <div style={{ display: 'grid', gap: '0.875rem' }} data-testid="my-page-services">
      <ItemsHeader kind="services" count={draft.length} />
      {draft.map((it, idx) => {
        const media = serviceImage(page, it.id);
        const imgKey = `img-service-${it.id}`;
        return (
          <div key={it.id} style={itemBox} data-testid={`my-page-services-item-${it.id}`}>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
              <div style={{ flex: 1, minWidth: 0, display: 'grid', gap: '0.75rem' }}>
                <TextInput
                  label={s('myPage.services.itemTitle')}
                  value={it.title || ''}
                  onChange={(e) => patch(idx, 'title', e.target.value)}
                  maxLength={LIMITS.service_title_max}
                  disabled={readOnly}
                  inputStyle={fieldStyle}
                  data-testid={`my-page-services-item-${it.id}-title`}
                />
                <Textarea
                  label={s('myPage.services.itemDescription')}
                  value={it.description_md || ''}
                  onChange={(e) => patch(idx, 'description_md', e.target.value)}
                  rows={3}
                  maxLength={LIMITS.service_description_max}
                  showCount
                  disabled={readOnly}
                  helperText={s('myPage.markdownHintShort')}
                  inputStyle={fieldStyle}
                  dir="auto"
                />
              </div>
              <RowActions
                index={idx} count={draft.length} disabled={readOnly}
                onMove={(i, d) => setDraft((items) => moveItem(items, i, d))}
                onRemove={(i) => setDraft((items) => items.filter((_, j) => j !== i))}
                testId={`my-page-services-item-${it.id}`}
              />
            </div>
            <ImageSlot
              slot="service"
              label={s('myPage.services.image')}
              hint={savedIds.has(it.id) ? s('myPage.services.imageHint') : s('myPage.services.imageAfterSave')}
              media={media}
              busy={!!cards[imgKey]?.saving}
              disabled={readOnly || !savedIds.has(it.id)}
              onUpload={async (image) => (await actions.uploadImage(imgKey, {
                slot: 'service', image, refId: it.id, alt: (it.title || '').trim() || sectionMeta('services').title,
              })).ok}
              onRemove={media ? async () => { await actions.removeImage(imgKey, media.id); } : undefined}
              testId={`my-page-services-item-${it.id}-image`}
            />
            <ImageErrors cards={cards} keys={[imgKey]} />
          </div>
        );
      })}
      {canAddItem('services', draft) && !readOnly ? (
        <Button
          variant="outline" size="sm"
          onClick={() => setDraft((items) => [...items, { id: newItemId(), title: '', description_md: '' }])}
          data-testid="my-page-services-add"
        >
          <Plus size={16} aria-hidden /> {s('myPage.services.add')}
        </Button>
      ) : null}
      <SaveRow
        status={saveStatus({ dirty, saving: card.saving, error: card.error, savedAt: card.savedAt })}
        error={card.error}
        onSave={() => saveItems(draft.map((it) => ({ id: it.id, title: it.title.trim(), description_md: it.description_md || '' })))}
        onReset={reset}
        disabled={readOnly || invalid}
        testId="my-page-services"
      />
    </div>
  );
}

// ── نظرات منتخب ─────────────────────────────────────────────────────────────

export function TestimonialsEditor({ section, actions, cards, readOnly }) {
  const s = useMyPageStrings();
  const fieldStyle = useFieldStyle();
  const { draft, setDraft, dirty, adopt, reset } = useItemsDraft(section);
  const saveItems = useItemsSave('testimonials', actions, adopt);
  const card = cards.testimonials || {};
  const patch = (idx, k, v) => setDraft((items) => items.map((it, i) => (i === idx ? { ...it, [k]: v } : it)));
  const invalid = draft.some((it) => !String(it.text || '').trim() || !it.consent_confirmed
    || overLimit(it.text, LIMITS.testimonial_text_max)
    || overLimit(it.attribution, LIMITS.testimonial_attribution_max))
    || draft.length > maxItems('testimonials');

  return (
    <div style={{ display: 'grid', gap: '0.875rem' }} data-testid="my-page-testimonials">
      <Alert variant="info" icon={<Info size={16} aria-hidden />} title={s('myPage.testimonials.framingTitle')}>
        {s('myPage.testimonials.framing')}
      </Alert>
      <ItemsHeader kind="testimonials" count={draft.length} />
      {draft.map((it, idx) => (
        <div key={it.id} style={itemBox} data-testid={`my-page-testimonials-item-${it.id}`}>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
            <div style={{ flex: 1, minWidth: 0, display: 'grid', gap: '0.75rem' }}>
              <Textarea
                label={s('myPage.testimonials.text')}
                value={it.text || ''}
                onChange={(e) => patch(idx, 'text', e.target.value)}
                rows={3}
                maxLength={LIMITS.testimonial_text_max}
                showCount
                disabled={readOnly}
                inputStyle={fieldStyle}
                dir="auto"
              />
              <TextInput
                label={s('myPage.testimonials.attribution')}
                placeholder={s('myPage.testimonials.attributionPlaceholder')}
                value={it.attribution || ''}
                onChange={(e) => patch(idx, 'attribution', e.target.value)}
                maxLength={LIMITS.testimonial_attribution_max}
                disabled={readOnly}
                inputStyle={fieldStyle}
              />
              <Checkbox
                checked={!!it.consent_confirmed}
                onChange={(v) => patch(idx, 'consent_confirmed', v === true)}
                label={s('myPage.testimonials.consent')}
                disabled={readOnly}
                data-testid={`my-page-testimonials-item-${it.id}-consent`}
              />
              {!it.consent_confirmed ? (
                <div style={{ fontSize: '0.75rem', color: C.textSecondary }}>{s('myPage.testimonials.consentRequired')}</div>
              ) : null}
            </div>
            <RowActions
              index={idx} count={draft.length} disabled={readOnly}
              onMove={(i, d) => setDraft((items) => moveItem(items, i, d))}
              onRemove={(i) => setDraft((items) => items.filter((_, j) => j !== i))}
              testId={`my-page-testimonials-item-${it.id}`}
            />
          </div>
        </div>
      ))}
      {canAddItem('testimonials', draft) && !readOnly ? (
        <Button
          variant="outline" size="sm"
          onClick={() => setDraft((items) => [...items, { id: newItemId(), text: '', attribution: '', consent_confirmed: false }])}
          data-testid="my-page-testimonials-add"
        >
          <Plus size={16} aria-hidden /> {s('myPage.testimonials.add')}
        </Button>
      ) : null}
      <SaveRow
        status={saveStatus({ dirty, saving: card.saving, error: card.error, savedAt: card.savedAt })}
        error={card.error}
        onSave={() => saveItems(draft.map((it) => ({
          id: it.id, text: it.text.trim(), attribution: (it.attribution || '').trim(), consent_confirmed: !!it.consent_confirmed,
        })))}
        onReset={reset}
        disabled={readOnly || invalid}
        testId="my-page-testimonials"
      />
    </div>
  );
}

// ── ویدیو ───────────────────────────────────────────────────────────────────

export function VideoEditor({ section, actions, cards, readOnly }) {
  const s = useMyPageStrings();
  const fieldStyle = useFieldStyle();
  const { draft, setDraft, dirty, adopt, reset } = useItemsDraft(section);
  const saveItems = useItemsSave('video', actions, adopt);
  const card = cards.video || {};
  const [url, setUrl] = useState('');
  const [caption, setCaption] = useState('');
  const patch = (idx, k, v) => setDraft((items) => items.map((it, i) => (i === idx ? { ...it, [k]: v } : it)));
  const invalid = draft.some((it) => overLimit(it.caption, LIMITS.video_caption_max)) || draft.length > maxItems('video');

  const add = () => {
    const u = url.trim();
    if (!u) return;
    setDraft((items) => [...items, { id: newItemId(), url: u, caption: caption.trim() }]);
    setUrl('');
    setCaption('');
  };

  return (
    <div style={{ display: 'grid', gap: '0.875rem' }} data-testid="my-page-video">
      <div style={{ fontSize: '0.8125rem', color: C.textSecondary }}>{s('myPage.video.intro')}</div>
      <ItemsHeader kind="video" count={draft.length} />
      {draft.map((it, idx) => (
        <div key={it.id} style={itemBox} data-testid={`my-page-video-item-${it.id}`}>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <PlayCircle size={18} aria-hidden style={{ color: C.navy, flexShrink: 0 }} />
            <span dir="ltr" style={{ flex: 1, minWidth: 0, fontSize: '0.8125rem', color: C.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', unicodeBidi: 'isolate' }}>
              {it.video_id
                ? `${it.provider === 'aparat' ? s('myPage.video.aparat') : s('myPage.video.youtube')} · ${it.video_id}`
                : it.url}
            </span>
            {!it.video_id ? (
              <span style={{ fontSize: '0.6875rem', color: C.textSecondary, flexShrink: 0 }}>{s('myPage.video.pending')}</span>
            ) : null}
            <RowActions
              index={idx} count={draft.length} disabled={readOnly}
              onMove={(i, d) => setDraft((items) => moveItem(items, i, d))}
              onRemove={(i) => setDraft((items) => items.filter((_, j) => j !== i))}
              testId={`my-page-video-item-${it.id}`}
            />
          </div>
          <TextInput
            label={s('myPage.video.caption')}
            value={it.caption || ''}
            onChange={(e) => patch(idx, 'caption', e.target.value)}
            maxLength={LIMITS.video_caption_max}
            disabled={readOnly}
            inputStyle={fieldStyle}
          />
        </div>
      ))}
      {canAddItem('video', draft) && !readOnly ? (
        <div style={{ ...itemBox, background: C.bg }}>
          <TextInput
            label={s('myPage.video.url')}
            placeholder="https://www.aparat.com/v/…"
            dir="ltr"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            inputStyle={fieldStyle}
            inputMode="url"
            data-testid="my-page-video-url"
          />
          <TextInput
            label={s('myPage.video.caption')}
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            maxLength={LIMITS.video_caption_max}
            inputStyle={fieldStyle}
          />
          <Counter value={caption} max={LIMITS.video_caption_max} />
          <Button variant="outline" size="sm" onClick={add} disabled={!url.trim()} data-testid="my-page-video-add">
            <Plus size={16} aria-hidden /> {s('myPage.video.add')}
          </Button>
        </div>
      ) : null}
      <SaveRow
        status={saveStatus({ dirty, saving: card.saving, error: card.error, savedAt: card.savedAt })}
        error={card.error}
        onSave={() => saveItems(draft.map((it) => (it.video_id
          ? { id: it.id, provider: it.provider, video_id: it.video_id, caption: it.caption || '' }
          : { id: it.id, url: it.url, caption: it.caption || '' })))}
        onReset={reset}
        disabled={readOnly || invalid}
        testId="my-page-video"
      />
    </div>
  );
}
